import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {Settings, Search, Plus, Edit, Trash2, Package, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, CheckCircle, Clock, AlertCircle, Check, TrendingUp, TrendingDown, MinusCircle, PlusCircle, FileText, Tag, Gauge} from 'lucide-react';
import InventaireListPageShell from './components/InventaireListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { InventaireModalPortal, InventaireViewModalPortal } from './components/inventaireModalPortal';
import FacturationFormModalTabs from '../facturation/components/FacturationFormModalTabs';
import {
  sliceQawaemStats,
  buildInventaireBulkDeleteFilterAction,
  InventaireTableSelectAllHeader,
  InventaireTableRowCheckbox,
  INVENTAIRE_TABLE_ACTIONS_TH_CLASS,
  buildInventaireImportExportProps,
  buildInventaireColumnVisibilityState,
  buildInventaireQawaemColumnDefs,
  isInventaireColumnVisible,
  inventaireTableColSpan,
  nextInventaireEntityRef,
  InventoryConfigNotice,
  useInventaireListSettings,
  sliceInventaireListItems,
  inventaireRecordInDateRange,
  INVENTAIRE_FORM_GRID,
  fetchInventaireWarehouses,
  buildInventaireWarehouseSelectOptions,
  buildInventaireProductSelectOptions,
  mapInventaireWarehouseSelection,
} from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { fetchCatalogProducts } from '../articles/catalogProductOptions';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import '../../shared/components/custom/CustomComponents.css';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { useTranslation } from 'react-i18next';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';

registerLocale('fr', fr);
registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

const AjustementsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.ajustementsPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const normalizeType = (type) => (type === 'réduction' ? 'reduction' : type);
  const { isDashboardLightMode } = useGlobalTheme();
  const { settings } = useSettings();
  const inventoryConfig = useInventoryConfig();
  useArticlesConfig();
  const { dateRange, viewMode, setViewMode, itemsPerPage } = useInventaireListSettings(inventoryConfig);
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnTypeButton = event.target.closest('[data-adjustments-type-dropdown-button]');
      const isClickInTypeDropdown = event.target.closest('[data-adjustments-type-dropdown]');
      if (!isClickOnTypeButton && !isClickInTypeDropdown) setShowTypeDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [adjustments, setAdjustments] = useState([]);
  const [physicalCounts, setPhysicalCounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [adjustmentFormTab, setAdjustmentFormTab] = useState('adjustmentInfo');

  const adjustmentFormTabs = useMemo(() => ([
    { id: 'adjustmentInfo', label: p('section_adjustment_info'), icon: Settings, iconColor: 'text-blue-400' },
    { id: 'productInfo', label: p('section_product_info'), icon: Package, iconColor: 'text-green-400' },
  ]), [p]);

  
  const [formData, setFormData] = useState({ 
    adjustment_number: '', source_inventory: '', adjustment_date: new Date().toISOString().split('T')[0],
    product_name: '', product_id: '', product_code: '', warehouse: '', warehouse_id: '', current_stock: '',
    adjusted_stock: '', adjustment_qty: '', adjustment_type: 'augmentation',
    reason: '', performed_by: '', notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_adjustments');
  const [quickFilter, setQuickFilter] = useState('all');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const [openTypeDropdown, setOpenTypeDropdown] = useState(null);
  const [typeDropdownPosition, setTypeDropdownPosition] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const normalizeAdjustmentStatus = (record) => {
    const raw = record?.status;
    if (raw === 'rejected' || raw === 'rejete' || raw === 'rejeté') return 'rejected';
    if (raw === 'pending' || raw === 'en_attente') return 'pending';
    if (raw === 'approved' || raw === 'approuve' || raw === 'approuvé') return 'approved';
    return record?.approved_by || record?.performed_by ? 'approved' : 'pending';
  };

  useEffect(() => { 
    loadAdjustments(); 
    loadPhysicalCounts();
    loadProducts();
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      setWarehouses(await fetchInventaireWarehouses());
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setWarehouses([]);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Click-outside handler for type dropdown
  useEffect(() => {
    if (!openTypeDropdown) return;
    
    const handleTypeDropdownClickOutside = (event) => {
      const clickedInsidePortal = event.target.closest('[data-type-dropdown]');
      const isClickingTypeButton = event.target.closest('[data-adjustment-type-button]');
      
      if (isClickingTypeButton) return;
      if (clickedInsidePortal) return;
      
      setOpenTypeDropdown(null);
      setTypeDropdownPosition(null);
    };

    const timer = setTimeout(() => {
      document.addEventListener('click', handleTypeDropdownClickOutside, true);
    }, 150);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleTypeDropdownClickOutside, true);
    };
  }, [openTypeDropdown]);

  useEffect(() => {
    if (!openTypeDropdown) return;
    
    const handleClickOutside = (event) => {
      const clickedInsidePortal = event.target.closest('[data-type-dropdown]');
      const clickedTypeButton = event.target.closest('button[class*="rounded-full"]');
      
      if (!clickedInsidePortal && !clickedTypeButton) {
        setOpenTypeDropdown(null);
        setTypeDropdownPosition(null);
      }
    };
    
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 150);
    
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [openTypeDropdown]);

  const loadAdjustments = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/adjustments');
      const data = response?.data || response || [];
      
      // Map backend fields to frontend format
      const typeMap = {
        'increase': 'augmentation',
        'decrease': 'reduction',
        'correction': 'correction'
      };
      
      const mappedData = data.map(adj => ({
        ...adj,
        current_stock: adj.quantity_before,
        adjusted_stock: adj.quantity_after,
        adjustment_qty: adj.quantity_adjusted,
        warehouse: adj.warehouse_name || adj.warehouse || adj.warehouse_id || '-',
        warehouse_id: adj.warehouse_id,
        adjustment_type: typeMap[adj.adjustment_type] || adj.adjustment_type,
        performed_by: adj.approved_by,
        status: adj.status || (adj.approved_by ? 'approved' : 'pending'),
      }));
      
      setAdjustments(sortRecordsNewestFirst(mappedData));
    } catch (error) {
      console.error('Error loading adjustments:', error);
      setAdjustments([]);
    }
  };

  const loadPhysicalCounts = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/physical-inventory');
      const data = response?.data || response || [];
      const mappedCounts = data.map((inv) => ({
        number: inv.inventory_number || inv.number,
        date: inv.inventory_date || inv.date,
        warehouse: inv.warehouse_name || inv.warehouse || '-',
        product_name: inv.product_name,
        product_code: inv.product_code,
        expected_quantity: inv.expected_quantity,
        counted_quantity: inv.counted_quantity,
        variance: inv.variance_quantity ?? ((inv.counted_quantity || 0) - (inv.expected_quantity || 0)),
        reason: inv.reason,
        counted_by: inv.counted_by,
      }));
      setPhysicalCounts(mappedCounts);
    } catch (error) {
      console.error('Error loading physical counts:', error);
      setPhysicalCounts([]);
    }
  };

  const loadProducts = async () => {
    try {
      setProducts(await fetchCatalogProducts());
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const handleInventorySelect = (inventoryNumber) => {
    const inventory = physicalCounts.find(i => i.number === inventoryNumber);
    if (inventory) {
      // Auto-fill all fields from the selected physical inventory count
      setFormData(prev => ({
        ...prev,
        source_inventory: inventory.number,
        adjustment_date: inventory.date,
        warehouse: inventory.warehouse,
        product_name: inventory.product_name,
        product_code: inventory.product_code,
        current_stock: inventory.expected_quantity?.toString(),
        adjustment_qty: Math.abs(inventory.variance)?.toString(),
        adjusted_stock: inventory.counted_quantity?.toString(),
        adjustment_type: inventory.variance > 0 ? 'augmentation' : 'reduction',
        reason: inventory.reason,
        performed_by: inventory.counted_by || '',
        notes: p('notes_based_on_inventory', { number: inventory.number })
      }));
    } else {
      // User typed a custom inventory number - just update that field
      setFormData(prev => ({ ...prev, source_inventory: inventoryNumber }));
    }
  };

  const stats = {
    total: adjustments.length,
    pending: adjustments.filter((a) => normalizeAdjustmentStatus(a) === 'pending').length,
    approved: adjustments.filter((a) => normalizeAdjustmentStatus(a) === 'approved').length,
    rejected: adjustments.filter((a) => normalizeAdjustmentStatus(a) === 'rejected').length,
    augmentation: adjustments.filter(a => a.adjustment_type === 'augmentation').length,
    reduction: adjustments.filter(a => a.adjustment_type === 'reduction' || a.adjustment_type === 'réduction').length,
    correction: adjustments.filter(a => a.adjustment_type === 'correction').length,
    total_adjusted: adjustments.reduce((sum, a) => sum + Math.abs(parseFloat(a.adjustment_qty) || 0), 0)
  };

  const statusOptions = [
    { value: 'pending', label: p('stat_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'approved', label: p('stat_approved'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'rejected', label: p('stat_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ];

  const mainTabs = [
    { id: 'all_adjustments', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_type', name: p('tab_by_type'), icon: Layers },
    { id: 'par_entrepot', name: p('tab_by_warehouse'), icon: Package }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'augmentation', name: p('type_augmentation'), icon: PlusCircle },
    { id: 'reduction', name: p('type_reduction'), icon: MinusCircle },
    { id: 'correction', name: p('type_correction'), icon: Settings }
  ];

  // Get unique warehouses for distribution
  const uniqueWarehouses = [...new Set(adjustments.map(a => a.warehouse).filter(Boolean))];
  const warehouseDistribution = uniqueWarehouses.slice(0, 6).map((warehouse, idx) => {
    const colors = [
      'bg-gradient-to-r from-blue-500 to-blue-600',
      'bg-gradient-to-r from-green-500 to-green-600',
      'bg-gradient-to-r from-blue-500 to-blue-600',
      'bg-gradient-to-r from-orange-500 to-orange-600',
      'bg-gradient-to-r from-red-500 to-red-600',
      'bg-gradient-to-r from-yellow-500 to-yellow-600'
    ];
    return {
      name: warehouse,
      count: adjustments.filter(a => a.warehouse === warehouse).length,
      color: colors[idx % colors.length]
    };
  });

  const adjustmentsDistribution = {
    all_adjustments: {
      title: p('overview_title'),
      data: [
        { filterKey: 'augmentation', name: p('type_augmentation'), count: stats.augmentation, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'reduction', name: p('type_reduction'), count: stats.reduction, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterKey: 'correction', name: p('type_correction'), count: stats.correction, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'inventory', name: p('dist_inventory'), count: adjustments.filter(a => {
          const reason = (a.reason || '').toLowerCase();
          return reason.includes('inventaire') || reason.includes('جرد') || reason.includes('inventory');
        }).length, color: "bg-gradient-to-r from-blue-500 to-blue-600" }
      ]
    },
    par_type: {
      title: p('distribution_by_type'),
      data: [
        { filterKey: 'augmentation', name: p('type_augmentation'), count: stats.augmentation, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'reduction', name: p('type_reduction'), count: stats.reduction, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterKey: 'correction', name: p('type_correction'), count: stats.correction, color: "bg-gradient-to-r from-orange-500 to-orange-600" }
      ]
    },
    par_entrepot: {
      title: p('distribution_by_warehouse'),
      data: warehouseDistribution.length > 0 ? warehouseDistribution.map(w => ({ ...w, filterKey: w.name })) : [
        { filterKey: 'none', name: p('no_warehouse'), count: 0, color: "bg-gradient-to-r from-gray-500 to-gray-600" }
      ]
    }
  };

  const distributionInfo = adjustmentsDistribution[mainViewMode] || adjustmentsDistribution.all_adjustments;

  const baseFilteredAdjustments = adjustments.filter(a => {
    if (!inventaireRecordInDateRange(dateRange, a.adjustment_date, a.created_at)) return false;

    if (searchTerm && !a.adjustment_number?.toLowerCase().includes(searchTerm.toLowerCase()) && !a.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !a.warehouse?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_adjustments') {
      if (quickFilter === 'augmentation' && a.adjustment_type !== 'augmentation') return false;
      if (quickFilter === 'reduction' && a.adjustment_type !== 'reduction' && a.adjustment_type !== 'réduction') return false;
      if (quickFilter === 'correction' && a.adjustment_type !== 'correction') return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_adjustments':
        case 'par_type':
          if (activeDistributionFilter === 'augmentation' && a.adjustment_type !== 'augmentation') return false;
          if (activeDistributionFilter === 'reduction' && a.adjustment_type !== 'reduction' && a.adjustment_type !== 'réduction') return false;
          if (activeDistributionFilter === 'correction' && a.adjustment_type !== 'correction') return false;
          if (activeDistributionFilter === 'inventory' && !(() => {
            const reason = (a.reason || '').toLowerCase();
            return reason.includes('inventaire') || reason.includes('جرد') || reason.includes('inventory');
          })()) return false;
          break;
        case 'par_entrepot':
          if (activeDistributionFilter !== 'none' && a.warehouse !== activeDistributionFilter) return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('adjustment_number'),
    { key: 'adjustment_date', getValue: (r) => new Date(r.adjustment_date).toLocaleDateString(dateLocale) },
    textColumnRule('product_name'),
    textColumnRule('warehouse'),
    textColumnRule('current_stock', (r) => String(r.current_stock ?? '')),
    textColumnRule('adjustment_qty', (r) => String(r.adjustment_qty ?? '')),
    { key: 'adjustment_type', getValue: (r) => normalizeType(r.adjustment_type), mode: 'exact' },
    exactColumnRule('status', (r) => normalizeAdjustmentStatus(r)),
  ], [dateLocale]);

  const filteredAdjustments = useMemo(
    () => applyColumnFilters(baseFilteredAdjustments, colFilters, columnFilterRules),
    [baseFilteredAdjustments, colFilters, columnFilterRules],
  );
  const visibleAdjustments = useMemo(
    () => sliceInventaireListItems(filteredAdjustments, itemsPerPage),
    [filteredAdjustments, itemsPerPage],
  );

  const generateAdjustmentNumber = useCallback(
    () => nextInventaireEntityRef(settings, 'adjustment', 'ADJ', adjustments, (a) => a.adjustment_number),
    [settings, adjustments],
  );

  const openNewAdjustmentModal = () => {
    if (!inventoryConfig.enableAdjustments) {
      window.alert(p('notice_adjustments_disabled', { defaultValue: 'التعديلات معطّلة من إعدادات المخزون.' }));
      return;
    }
    setEditingRecord(null);
    setAdjustmentFormTab('adjustmentInfo');
    setFormData({
      adjustment_number: inventoryConfig.autoGenerateAdjustmentReference !== false ? generateAdjustmentNumber() : '',
      source_inventory: '',
      adjustment_date: new Date().toISOString().split('T')[0],
      product_name: '',
      product_id: '',
      product_code: '',
      warehouse: '',
      warehouse_id: '',
      current_stock: '',
      adjusted_stock: '',
      adjustment_qty: '',
      adjustment_type: 'augmentation',
      reason: '',
      performed_by: '',
      notes: '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inventoryConfig.requireReasonForAdjustment && !(formData.reason || '').trim()) {
      notifyError(showNotify, p('reason_required', { defaultValue: 'السبب مطلوب للتعديل — راجع إعدادات المخزون.' }));
      return;
    }
    try {
      const adjustmentNumber = (formData.adjustment_number || '').trim() || generateAdjustmentNumber();
      // Map French adjustment types to English for database
      const adjustmentTypeMap = {
        'augmentation': 'increase',
        'reduction': 'decrease',
        'réduction': 'decrease',
        'correction': 'correction'
      };
      
      const submitData = { 
        adjustment_number: adjustmentNumber,
        product_id: formData.product_id,
        product_name: formData.product_name,
        warehouse_id: formData.warehouse_id || null,
        warehouse_name: formData.warehouse || formData.warehouse_name || null,
        adjustment_type: adjustmentTypeMap[formData.adjustment_type] || 'correction',
        quantity_before: parseFloat(formData.current_stock) || 0,
        quantity_after: parseFloat(formData.adjusted_stock) || 0,
        quantity_adjusted: parseFloat(formData.adjustment_qty) || 0,
        reason: formData.reason,
        adjustment_date: formData.adjustment_date,
        approved_by: formData.performed_by || null,
        notes: formData.notes || null
      };
      if (editingRecord) {
        await localApiAdapter.put(`/inventory/adjustments/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'التعديل' }));
      } else {
        await localApiAdapter.post('/inventory/adjustments', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'التعديل' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      return;
    } finally {
      resetForm();
      loadAdjustments();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      adjustment_number: record.adjustment_number || '', adjustment_date: record.adjustment_date || new Date().toISOString().split('T')[0],
      product_name: record.product_name || '', product_id: record.product_id || '', product_code: record.product_code || '',
      warehouse: record.warehouse_name || record.warehouse || '', warehouse_id: record.warehouse_id?.toString() || '',
      current_stock: record.current_stock?.toString() || '',
      adjusted_stock: record.adjusted_stock?.toString() || '', adjustment_qty: record.adjustment_qty?.toString() || '',
      adjustment_type: normalizeType(record.adjustment_type) || 'augmentation', reason: record.reason || '',
      performed_by: record.performed_by || '', notes: record.notes || ''
    });
    setAdjustmentFormTab('adjustmentInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/inventory/adjustments/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'التعديل' }));
        loadAdjustments();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const filteredAdjustmentsIds = useMemo(() => filteredAdjustments.map((r) => r.id), [filteredAdjustments]);
  const allRowsSelected = filteredAdjustmentsIds.length > 0 && filteredAdjustmentsIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredAdjustmentsIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };
  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/inventory/adjustments/${id}`)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label', { defaultValue: 'التعديل' }));
        setSelectedRecords([]);
        loadAdjustments();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      adjustment_number: '', adjustment_date: new Date().toISOString().split('T')[0],
      product_name: '', product_id: '', product_code: '', warehouse: '', warehouse_id: '', current_stock: '',
      adjusted_stock: '', adjustment_qty: '', adjustment_type: 'augmentation',
      reason: '', performed_by: '', notes: ''
    });
    setEditingRecord(null);
    setAdjustmentFormTab('adjustmentInfo');
    setShowModal(false);
  };


  // Import/Export handlers
  const handleImport = () => {
    triggerFileInput((file) => {
      importFromFile(file, (data) => {
        console.log('Imported data:', data);
        // Process import data here
        loadAdjustments();
      });
    });
  };

  const handleExport = () => {
    exportToCSV(filteredAdjustments, 'ajustements_stock');
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      await generateReportPDF(
        p('report_title'),
        filteredAdjustments,
        ['adjustment_number', 'adjustment_date', 'product_name', 'warehouse', 'adjustment_type'],
        'ajustements_stock'
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    setIsGeneratingExcel(true);
    try {
      await generateReportExcel(
        p('report_title'),
        filteredAdjustments,
        ['adjustment_number', 'adjustment_date', 'product_name', 'warehouse', 'adjustment_type'],
        'ajustements_stock'
      );
    } catch (error) {
      console.error('Error generating Excel:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const typeOptions = [
    { value: 'augmentation', label: p('type_augmentation'), icon: PlusCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'reduction', label: p('type_reduction'), icon: MinusCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'correction', label: p('type_correction'), icon: Settings, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' }
  ];

  const getTypeLabel = (type) => {
    const normalized = normalizeType(type);
    const option = typeOptions.find(s => s.value === normalized);
    return option?.label || type;
  };

  const getTypeBadge = (type) => {
    const normalized = normalizeType(type);
    const config = typeOptions.find(s => s.value === normalized) || typeOptions[0];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 border ${style}`}>
        <Icon size={14} />{label}
      </span>
    );
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const payload = {};
      if (newStatus === 'approved') {
        payload.approved_by = 'system';
      } else if (newStatus === 'pending') {
        payload.approved_by = '';
      } else if (newStatus === 'rejected') {
        payload.approved_by = 'rejected';
      }
      await localApiAdapter.put(`/inventory/adjustments/${recordId}`, payload);
      await loadAdjustments();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleTypeChange = async (recordId, newType) => {
    try {
      // Map French type to English for backend
      const typeMap = {
        'augmentation': 'increase',
        'reduction': 'decrease',
        'réduction': 'decrease',
        'correction': 'correction'
      };
      
      await localApiAdapter.put(`/inventory/adjustments/${recordId}`, { 
        adjustment_type: typeMap[newType] 
      });
      setOpenTypeDropdown(null);
      setTypeDropdownPosition(null);
      await loadAdjustments();
    } catch (error) {
      console.error('Error updating type:', error);
    }
  };

  const ClickableTypeBadge = ({ type, recordId }) => {
    const config = typeOptions.find(s => s.value === normalizeType(type)) || typeOptions[0];
    const { label, icon: Icon, style } = config;
    const buttonRef = React.useRef(null);
    
    const handleToggle = (e) => {
      e.stopPropagation();
      if (openTypeDropdown === recordId) {
        setOpenTypeDropdown(null);
        setTypeDropdownPosition(null);
      } else {
        const rect = buttonRef.current.getBoundingClientRect();
        setTypeDropdownPosition({
          top: rect.bottom + window.scrollY + 5,
          left: rect.left + window.scrollX + (rect.width / 2)
        });
        setOpenTypeDropdown(recordId);
      }
    };
    
    return (
      <>
        <button
          ref={buttonRef}
          data-adjustment-type-button
          onClick={handleToggle}
          className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style} hover:opacity-80 transition-opacity cursor-pointer`}
        >
          <Icon size={14} />{label}
        </button>
        
        {openTypeDropdown === recordId && typeDropdownPosition && ReactDOM.createPortal(
          <div 
            data-type-dropdown
            style={{
              position: 'absolute',
              top: `${typeDropdownPosition.top}px`,
              left: `${typeDropdownPosition.left}px`,
              transform: 'translateX(-50%)',
              zIndex: 9999
            }}
            className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden min-w-[200px] bg-gradient-to-br from-slate-900/98 via-blue-900/95 to-slate-900/98 backdrop-blur-xl"
          >
            {typeOptions.map((option) => (
              <button
                key={option.value}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleTypeChange(recordId, option.value);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left cursor-pointer ${option.value === normalizeType(type) ? 'bg-blue-500/25 shadow-inner' : ''}`}
              >
                <option.icon size={16} className={option.style.includes('green') ? 'text-green-400' : option.style.includes('red') ? 'text-red-400' : 'text-orange-400'} />
                <span className="text-white font-medium text-sm">{option.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
      </>
    );
  };

  const INVENTAIRE_TABLE_COLUMNS = [
    { key: 'adjustment_number', labelKey: 'col_adjustment_number' },
    { key: 'adjustment_date', labelKey: 'col_date' },
    { key: 'product_name', labelKey: 'col_product' },
    { key: 'warehouse', labelKey: 'col_warehouse' },
    { key: 'current_stock', labelKey: 'col_current_stock' },
    { key: 'adjustment_qty', labelKey: 'col_adjustment' },
    { key: 'adjustment_type', labelKey: 'col_type' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildInventaireColumnVisibilityState(INVENTAIRE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildInventaireQawaemColumnDefs(INVENTAIRE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isInventaireColumnVisible(columnVisibility, key);
  const tableColSpan = inventaireTableColSpan(columnVisibility);

  const inventaireStatEntries = useMemo(() => sliceQawaemStats([
    { key: 'stat_total', label: p('stat_total'), value: stats.total, icon: Gauge },
    { key: 'stat_pending', label: p('stat_pending'), value: stats.pending, icon: Clock },
    { key: 'stat_approved', label: p('stat_approved'), value: stats.approved, icon: CheckCircle },
    { key: 'stat_rejected', label: p('stat_rejected'), value: stats.rejected, icon: XCircle },
  ], 4), [stats, p]);


  const importExportProps = useMemo(() => buildInventaireImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    importLabel: t('common.import'),
    exportLabel: t('common.export'),
    generatingLabel: p('generating'),
    isGeneratingPDF,
    handlers: {
      onImport: handleImport,
      onExportCSV: handleExport,
      onExportPDF: handleExportPDF,
      onExportExcel: handleExportExcel,
    },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, t, p]);

  return (
    <>
    <InventoryConfigNotice
      message={!inventoryConfig.enableAdjustments ? p('notice_adjustments_disabled_banner', { defaultValue: 'التعديلات معطّلة — فعّلها من إعدادات المخزون ← التلف والفاقد.' }) : null}
    />
    <InventaireListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Gauge}
      statEntries={inventaireStatEntries}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      onCreate={openNewAdjustmentModal}
      createLabel={p('new_adjustment')}
      importExport={importExportProps}
      filterActions={buildInventaireBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, p })}
    >
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <InventaireTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onSelectAll={handleSelectAll} p={p} />
                  {isColVisible('adjustment_number') && (

                    <TableColumnHeaderFilter columnKey="adjustment_number" label={p('col_adjustment_number')} value={colFilters.adjustment_number ?? ''} onChange={(v) => setColFilter('adjustment_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('adjustment_date') && (

                    <TableColumnHeaderFilter columnKey="adjustment_date" label={p('col_date')} value={colFilters.adjustment_date ?? ''} onChange={(v) => setColFilter('adjustment_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('product_name') && (

                    <TableColumnHeaderFilter columnKey="product_name" label={p('col_product')} value={colFilters.product_name ?? ''} onChange={(v) => setColFilter('product_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('warehouse') && (

                    <TableColumnHeaderFilter columnKey="warehouse" label={p('col_warehouse')} value={colFilters.warehouse ?? ''} onChange={(v) => setColFilter('warehouse', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('current_stock') && (

                    <TableColumnHeaderFilter columnKey="current_stock" label={p('col_current_stock')} value={colFilters.current_stock ?? ''} onChange={(v) => setColFilter('current_stock', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('adjustment_qty') && (

                    <TableColumnHeaderFilter columnKey="adjustment_qty" label={p('col_adjustment')} value={colFilters.adjustment_qty ?? ''} onChange={(v) => setColFilter('adjustment_qty', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('adjustment_type') && (

                    <TableColumnHeaderFilter columnKey="adjustment_type" label={p('col_type')} kind="select" options={typeOptions.map((o) => ({ value: o.value, label: o.label }))} value={colFilters.adjustment_type ?? ''} onChange={(v) => setColFilter('adjustment_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  {isColVisible('status') && (

                    <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={INVENTAIRE_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {visibleAdjustments.length > 0 ? (
                  visibleAdjustments.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <InventaireTableRowCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {isColVisible('adjustment_number') && (

                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.adjustment_number}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('performed_by_prefix')} {record.performed_by || 'N/A'}</span>
                        </div>
                      </td>

                      )}
                      {isColVisible('adjustment_date') && (

                        <td className="px-6 py-4">
                        <span className="text-white font-semibold">{new Date(record.adjustment_date).toLocaleDateString(dateLocale)}</span>
                      </td>

                      )}
                      {isColVisible('product_name') && (

                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.product_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('code_prefix')} {record.product_code || 'N/A'}</span>
                        </div>
                      </td>

                      )}
                      {isColVisible('warehouse') && (

                        <td className="px-6 py-4">
                        <span className="text-white font-semibold">{record.warehouse || '-'}</span>
                      </td>

                      )}
                      {isColVisible('current_stock') && (

                        <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-semibold">{record.current_stock || 0}</span>
                          <span className="text-white/50">→</span>
                          <span className="text-white font-semibold">{record.adjusted_stock || 0}</span>
                        </div>
                      </td>

                      )}
                      {isColVisible('adjustment_qty') && (

                        <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          {record.adjustment_type === 'augmentation' && <PlusCircle size={14} className="text-green-400" />}
                          {(record.adjustment_type === 'reduction' || record.adjustment_type === 'réduction') && <MinusCircle size={14} className="text-red-400" />}
                          {record.adjustment_type === 'correction' && <Settings size={14} className="text-orange-400" />}
                          <span className={`font-semibold ${record.adjustment_type === 'augmentation' ? 'text-green-400' : (record.adjustment_type === 'reduction' || record.adjustment_type === 'réduction') ? 'text-red-400' : 'text-orange-400'}`}>
                            {record.adjustment_qty > 0 ? '+' : ''}{record.adjustment_qty || 0}
                          </span>
                        </div>
                      </td>

                      )}
                      {isColVisible('adjustment_type') && (

                        <td className="px-6 py-4 text-center"><div className="flex justify-center">{getTypeBadge(record.adjustment_type)}</div></td>

                      )}
                      {isColVisible('status') && (

                        <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={normalizeAdjustmentStatus(record)} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={t('common.change_status')} /></div></td>

                      )}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Settings size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {visibleAdjustments.length > 0 ? (
              visibleAdjustments.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Settings size={20} className="text-blue-400" />
                          {record.adjustment_number}
                        </h3>
                        {getTypeBadge(record.adjustment_type)}
                        <QawaemClickableStatusBadge status={normalizeAdjustmentStatus(record)} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={t('common.change_status')} />
                        <span className="text-white/50 text-sm">{new Date(record.adjustment_date).toLocaleDateString(dateLocale)}</span>
                      </div>
                      <div className={INVENTAIRE_FORM_GRID}>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_product')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}</span> {record.product_name || '-'}</div>
                            <div><span className="font-medium">{p('label_code')}</span> {record.product_code || 'N/A'}</div>
                            <div><span className="font-medium">{p('label_warehouse')}</span> {record.warehouse || 'N/A'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_stock')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_current')}</span> {record.current_stock || 0}</div>
                            <div><span className="font-medium">{p('label_adjusted')}</span> {record.adjusted_stock || 0}</div>
                            <div className="flex items-center gap-1">
                              <span className="font-medium">{p('label_variation')}</span>
                              <span className={`font-bold ${record.adjustment_qty > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {record.adjustment_qty > 0 ? '+' : ''}{record.adjustment_qty || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_reason')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div>{record.reason || p('reason_unspecified')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_performed_by')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div>{record.performed_by || 'N/A'}</div>
                            {record.notes && <div className="mt-2 p-2 bg-white/5 rounded"><span className="font-medium text-white/80 text-xs">{p('notes_label')}</span><p className="text-white/60 text-xs mt-1">{record.notes}</p></div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><Settings size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
    </InventaireListPageShell>

      
      {/* Edit/Create Modal */}
      {showModal && (
        <InventaireModalPortal show={showModal} onClose={resetForm} maxWidth="1200px">
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('edit_adjustment') : p('new_adjustment')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="adjustment-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            
            
            <FacturationFormModalTabs
              tabs={adjustmentFormTabs}
              activeTab={adjustmentFormTab}
              onTabChange={setAdjustmentFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('edit_adjustment') : p('new_adjustment')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="adjustment-form" onSubmit={handleSubmit}>
              {adjustmentFormTab === 'adjustmentInfo' && (
              <div className={INVENTAIRE_FORM_GRID}><div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right flex items-center gap-2">
                              <FileText size={16} className="text-yellow-400" />
                              {p('physical_inventory_label')}
                            </label>
                            <CustomSelect
                              value={formData.source_inventory}
                              onChange={handleInventorySelect}
                              options={physicalCounts.map(count => ({
                                value: count.number,
                                label: p('count_option', {
                                  number: count.number,
                                  date: count.date,
                                  product: count.product_name,
                                  variance: `${count.variance > 0 ? '+' : ''}${count.variance}`
                                })
                              }))}
                              placeholder={p('placeholder_count')}
                              searchable={true}
                              creatable={true}
                            />
                          </div>
                          
                          <div className={INVENTAIRE_FORM_GRID}>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_adjustment_number', { defaultValue: 'رمز التعديل' })}</label>
                            <input
                              type="text"
                              required
                              value={formData.adjustment_number || ''}
                              onChange={(e) => setFormData({ ...formData, adjustment_number: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                              placeholder={p('placeholder_adjustment_number', { defaultValue: 'ADJ-001' })}
                              dir="ltr"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date')} <span className="qawaem-required-mark">*</span></label>
                            <CustomDatePicker
                              value={formData.adjustment_date}
                              onChange={(date) => setFormData({ ...formData, adjustment_date: date })}
                              placeholder={p('placeholder_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_type')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.adjustment_type}
                              onChange={(value) => setFormData({ ...formData, adjustment_type: value })}
                              options={[
                                { value: 'augmentation', label: p('type_augmentation') },
                                { value: 'reduction', label: p('type_reduction') },
                                { value: 'correction', label: p('type_correction') }
                              ]}
                              placeholder={p('placeholder_type')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_warehouse_required')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.warehouse_id}
                              onChange={(value) => {
                                const mapped = mapInventaireWarehouseSelection(value, warehouses);
                                setFormData({
                                  ...formData,
                                  warehouse_id: mapped.warehouse_id,
                                  warehouse: mapped.warehouse_name,
                                });
                              }}
                              options={buildInventaireWarehouseSelectOptions(warehouses)}
                              placeholder={p('placeholder_warehouse')}
                              searchable={true}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reason')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.reason}
                              onChange={(value) => setFormData({ ...formData, reason: value })}
                              options={[
                                { value: p('reason_inventory_correction'), label: p('reason_inventory_correction') },
                                { value: p('reason_damaged'), label: p('reason_damaged') },
                                { value: p('reason_expired'), label: p('reason_expired') },
                                { value: p('reason_loss'), label: p('reason_loss') },
                                { value: p('reason_theft'), label: p('reason_theft') },
                                { value: p('reason_found'), label: p('reason_found') },
                                { value: p('reason_customer_return'), label: p('reason_customer_return') }
                              ]}
                              placeholder={p('placeholder_reason')}
                              searchable={true}
                              creatable={true}
                            />
                          </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_performed_by')}</label>
                              <CustomSelect
                                value={formData.performed_by}
                                onChange={(value) => setFormData({ ...formData, performed_by: value })}
                                options={[
                                  { value: p('role_stock_manager'), label: p('role_stock_manager') },
                                  { value: p('role_warehouse_manager'), label: p('role_warehouse_manager') },
                                  { value: p('role_supervisor'), label: p('role_supervisor') }
                                ]}
                                placeholder={p('placeholder_user')}
                                searchable={true}
                                creatable={true}
                              />
                            </div>
                          </div></div>
              )}

              {adjustmentFormTab === 'productInfo' && (
              <div className={INVENTAIRE_FORM_GRID}><div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_product_name')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.product_id}
                              onChange={(value) => {
                                const selectedProduct = products.find((prod) => String(prod.id) === String(value));
                                if (selectedProduct) {
                                  setFormData({ 
                                    ...formData, 
                                    product_id: value,
                                    product_name: selectedProduct.name || '',
                                    product_code: selectedProduct.reference || '',
                                    current_stock: selectedProduct.stock?.toString() || '0',
                                  });
                                } else {
                                  setFormData({ ...formData, product_id: value || '', product_name: '' });
                                }
                              }}
                              options={buildInventaireProductSelectOptions(products)}
                              placeholder={p('placeholder_product')}
                              searchable={true}
                            />
                          </div>
                          
                          <div className="border-t border-white/10 pt-4 md:col-span-3">
                            <div className={INVENTAIRE_FORM_GRID}>
                              <div>
                                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_current_stock')}</label>
                                <input type="number" value={formData.current_stock} onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder="100" />
                              </div>
                              <div>
                                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_stock_after')}</label>
                                <input 
                                  type="number" 
                                  value={formData.adjusted_stock} 
                                  onChange={(e) => {
                                    const adjustedStock = parseFloat(e.target.value) || 0;
                                    const currentStock = parseFloat(formData.current_stock) || 0;
                                    const difference = adjustedStock - currentStock;
                                    const adjustmentQty = Math.abs(difference);
                                    const adjustmentType = difference > 0 ? 'augmentation' : difference < 0 ? 'reduction' : 'correction';
                                    
                                    setFormData({ 
                                      ...formData, 
                                      adjusted_stock: e.target.value,
                                      adjustment_qty: adjustmentQty.toString(),
                                      adjustment_type: adjustmentType
                                    });
                                  }} 
                                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                                  placeholder="110" 
                                />
                              </div>
                              <div>
                                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_adjustment_qty')} <span className="qawaem-required-mark">*</span></label>
                                <input type="number" required value={formData.adjustment_qty} onChange={(e) => setFormData({ ...formData, adjustment_qty: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder="10" />
                              </div>
                            </div>
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label>
                            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all h-24 resize-none" placeholder={p('placeholder_notes')}></textarea>
                          </div></div>
              )}
              </form>
            </div>
        </InventaireModalPortal>
      )}

      {/* View Modal */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)' }}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{p('view_title')}</h2>
                <p className="text-white/60">{p('view_number', { number: viewingRecord.adjustment_number })}</p>
              </div>
              <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
            </div>
            <div className="space-y-4 text-white">
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="font-semibold mb-3 text-blue-300">{p('view_section_info')}</h3>
                <div className={`${INVENTAIRE_FORM_GRID} text-sm`}>
                  <div><span className="text-white/60">{p('view_label_date')}</span> <span className="font-medium">{viewingRecord.adjustment_date}</span></div>
                  <div><span className="text-white/60">{p('view_label_type')}</span> <span className="font-medium">{getTypeLabel(viewingRecord.adjustment_type)}</span></div>
                  <div><span className="text-white/60">{p('view_label_warehouse')}</span> <span className="font-medium">{viewingRecord.warehouse}</span></div>
                  <div><span className="text-white/60">{p('view_label_reason')}</span> <span className="font-medium">{viewingRecord.reason}</span></div>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="font-semibold mb-3 text-green-300">{p('view_section_product')}</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="text-white/60">{p('view_label_product')}</span> <span className="font-medium">{viewingRecord.product_name}</span></div>
                  <div><span className="text-white/60">{p('view_label_adjustment_qty')}</span> <span className="font-medium">{viewingRecord.adjustment_qty}</span></div>
                  <div><span className="text-white/60">{p('view_label_stock_after')}</span> <span className="font-medium">{viewingRecord.adjusted_stock}</span></div>
                </div>
              </div>
              {viewingRecord.notes && (
                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="font-semibold mb-2 text-yellow-300">{p('section_notes')}</h3>
                  <p className="text-sm text-white/80">{viewingRecord.notes}</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default AjustementsPage;


